import { useMemo, useState } from "react";
import { type ColumnDef } from "@tanstack/react-table";
import { Globe, RefreshCw, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { PageHeader } from "@/components/shared/page-header";
import { DataTable } from "@/components/shared/data-table";
import { EmptyState } from "@/components/shared/empty-state";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, Skeleton } from "@fox1ck-org/ui";
import {
  useEnvironments,
  useEnvironmentDetail,
} from "@/hooks/use-environments";
import type { EnvironmentResource } from "@/api/environments";
import { healthLabel } from "@/components/shared/status-badge";

const PHASE_LABELS: Record<string, string> = { Ready: "Готово", Pending: "Ожидание" };

function phaseLabel(phase: string): string {
  return PHASE_LABELS[phase] ?? healthLabel(phase);
}

function PhaseBadge({ phase, healthStatus }: { phase?: string; healthStatus?: string }) {
  const effective = phase ?? healthStatus ?? "Unknown";
  if (effective === "Ready" || effective === "Healthy") {
    return (
      <Badge variant="primary" className="gap-1">
        <CheckCircle2 className="h-3 w-3" /> {phaseLabel(effective)}
      </Badge>
    );
  }
  if (effective === "Degraded") {
    return (
      <Badge variant="danger" className="gap-1">
        <AlertCircle className="h-3 w-3" /> Деградация
      </Badge>
    );
  }
  if (effective === "Pending" || effective === "Progressing") {
    return (
      <Badge variant="neutral" className="gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> {phaseLabel(effective)}
      </Badge>
    );
  }
  return <Badge variant="outline">{phaseLabel(effective)}</Badge>;
}

function TierBadge({ tier }: { tier?: string }) {
  if (!tier) return null;
  const variant =
    tier === "production"
      ? "primary"
      : tier === "staging"
        ? "neutral"
        : "outline";
  return <Badge variant={variant}>{tier}</Badge>;
}

function LabelCluster({ labels }: { labels?: Record<string, string> }) {
  if (!labels) return null;
  const entries = Object.entries(labels);
  if (entries.length === 0) return <span className="text-muted-foreground text-xs">—</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {entries.map(([k, v]) => (
        <Badge key={`${k}=${v}`} variant="outline" className="font-mono text-[10px]">
          {k}={v}
        </Badge>
      ))}
    </div>
  );
}

const columns: ColumnDef<EnvironmentResource>[] = [
  {
    accessorKey: "spec.app",
    header: "Приложение",
    cell: ({ row }) => (
      <span className="font-mono text-sm font-medium">{row.original.spec.app}</span>
    ),
  },
  {
    accessorKey: "spec.envName",
    header: "Окружение",
    cell: ({ row }) => (
      <span className="font-mono text-sm font-medium">{row.original.spec.envName}</span>
    ),
  },
  {
    accessorKey: "spec.tier",
    header: "Уровень",
    cell: ({ row }) => <TierBadge tier={row.original.spec.tier} />,
  },
  {
    accessorKey: "spec.dbSchema",
    header: "Схема БД",
    cell: ({ row }) => (
      <span className="font-mono text-xs text-muted-foreground">
        {row.original.spec.dbSchema ?? "—"}
      </span>
    ),
  },
  {
    accessorKey: "spec.hostnames",
    header: "Хосты",
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        {(row.original.spec.hostnames ?? []).map((h) => (
          <span key={h} className="font-mono text-xs">
            {h}
          </span>
        ))}
      </div>
    ),
  },
  {
    accessorKey: "status.phase",
    header: "Фаза",
    cell: ({ row }) => <PhaseBadge phase={row.original.status?.phase} />,
  },
  {
    accessorKey: "spec.labels",
    header: "Метки",
    cell: ({ row }) => <LabelCluster labels={row.original.spec.labels} />,
  },
];

function EnvironmentSheet({
  app,
  envName,
  open,
  onOpenChange,
}: {
  app: string | undefined;
  envName: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data, isLoading } = useEnvironmentDetail(app, envName);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="font-mono">
            {app}/{envName}
          </SheetTitle>
          <SheetDescription>
            Только чтение: Environment CR, соответствующее приложение ArgoCD и размеченные нагрузки.
          </SheetDescription>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="space-y-3 mt-6">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : (
          <div className="space-y-4 mt-6">
            <DetailSection title="Спецификация">
              <KeyValueGrid
                rows={[
                  ["app", data.spec.app],
                  ["envName", data.spec.envName],
                  ["tier", data.spec.tier ?? "—"],
                  ["dbSchema", data.spec.dbSchema ?? "—"],
                  ["clickhouseDb", data.spec.clickhouseDb ?? "—"],
                  ["keycloakClient", data.spec.keycloakClient ?? "—"],
                  ["secretBundle", data.spec.secretBundle ?? "—"],
                  ["temporalNamespace", data.spec.temporalNamespace ?? "—"],
                  ["hostnames", (data.spec.hostnames ?? []).join(", ")],
                  [
                    "replicas",
                    `api=${data.spec.replicas?.api ?? "?"} worker=${data.spec.replicas?.worker ?? "?"}`,
                  ],
                ]}
              />
            </DetailSection>

            <DetailSection title="Метки">
              <LabelCluster labels={data.spec.labels} />
            </DetailSection>

            <DetailSection title="ArgoCD">
              {data.argocd ? (
                <KeyValueGrid
                  rows={[
                    ["application", data.argocd.name],
                    ["sync", data.argocd.syncStatus ?? "—"],
                    ["health", data.argocd.healthStatus ?? "—"],
                    ["revision", data.argocd.revision?.slice(0, 12) ?? "—"],
                    ["syncedAt", data.argocd.syncedAt ?? "—"],
                  ]}
                />
              ) : (
                <span className="text-muted-foreground text-sm">Приложения ArgoCD пока нет.</span>
              )}
            </DetailSection>

            <DetailSection title={`Нагрузки (${data.workloads.length})`}>
              {data.workloads.length === 0 ? (
                <span className="text-muted-foreground text-sm">Размеченных пока нет.</span>
              ) : (
                <div className="space-y-2">
                  {data.workloads.map((w) => (
                    <Card key={w.name}>
                      <CardContent className="pt-4 pb-4 flex items-center justify-between">
                        <div className="space-y-1">
                          <div className="font-mono text-sm">{w.name}</div>
                          <div className="text-xs text-muted-foreground">
                            component={w.component ?? "—"}
                          </div>
                        </div>
                        <div className="font-mono text-xs">
                          готово {w.ready}/{w.replicas} · доступно {w.available}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </DetailSection>

            <DetailSection title={`Поды (${data.pods.length})`}>
              {data.pods.length === 0 ? (
                <span className="text-muted-foreground text-sm">Нет.</span>
              ) : (
                <div className="space-y-1">
                  {data.pods.map((p) => (
                    <div
                      key={p.name}
                      className="flex items-center justify-between font-mono text-xs px-3 py-2 rounded-md bg-muted/30"
                    >
                      <span>{p.name}</span>
                      <span className="text-muted-foreground">
                        {p.phase} · {p.nodeName ?? "?"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </DetailSection>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailSection({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">{title}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function KeyValueGrid({ rows }: { rows: Array<[string, string]> }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs font-mono">
      {rows.map(([k, v]) => (
        <div key={k} className="contents">
          <span className="text-muted-foreground">{k}</span>
          <span className="truncate">{v}</span>
        </div>
      ))}
    </div>
  );
}

export default function Environments() {
  const { data, isLoading, refetch, isFetching } = useEnvironments();
  const items = data?.items ?? [];
  const [selected, setSelected] = useState<EnvironmentResource | null>(null);
  const [appFilter, setAppFilter] = useState<string>("__all__");

  const apps = useMemo(() => {
    const s = new Set<string>();
    items.forEach((e) => s.add(e.spec.app));
    return Array.from(s).sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (appFilter === "__all__") return items;
    return items.filter((e) => e.spec.app === appFilter);
  }, [items, appFilter]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Окружения"
        description="Логические срезы нагрузок приложений. Только чтение: источник правды лежит в репозитории инфраструктуры (clusters/base/{app}/environments/)."
      >
        <Button
          variant="ghost"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
          Обновить
        </Button>
      </PageHeader>

      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Приложение:</span>
        <Select value={appFilter} onValueChange={setAppFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Все приложения</SelectItem>
            {apps.map((a) => (
              <SelectItem key={a} value={String(a)}>
                {a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={<Globe className="h-8 w-8" />}
          title="Окружений нет"
          description="Положите YAML с Environment CR в clusters/base/{app}/environments/ репозитория инфраструктуры. ArgoCD подхватит его сам."
        />
      ) : (
        <DataTable
          columns={columns}
          data={filtered}
          onRowClick={(row) => setSelected(row)}
        />
      )}

      <EnvironmentSheet
        app={selected?.spec.app}
        envName={selected?.spec.envName}
        open={!!selected}
        onOpenChange={(open) => !open && setSelected(null)}
      />
    </div>
  );
}
